import { useCallback, useEffect, useState } from 'react'
import { NavigationBar } from '../navigation-bar/navigation-bar'
import './styles.scss'
import { fetchGameConfig } from '@AppState/game-config'
import { useSelector } from 'react-redux'
import { RootState, useAppDispatch } from '@AppState/redux-store'
import { fetchLatestDraw } from '@AppState/latest-draw'
import { RapidGameDataServiceAPI } from '@API/gambyt-data-services/rapid-games-api'
import { KenoDataServiceAPI } from '@API/gambyt-data-services/keno-api'
import { GameController } from '@Animation/game-control'
import { fetchHostAndAppConfig } from '@AppState/app-settings'
import { LoadingAnimation } from '@Components/loading/loading'
import { ContainerMessenger, ContainerMessage } from '@Util/container-messenger'
import { AppConfigLoader } from '@Components/app-config/app-config'
import Analytics from '@Util/analytics'
import { ANIMATION_CANVAS_STATE } from '@AppState/animation'
import { NAV_VALUES } from '@Components/navigation-bar/types'
import { ViewModeActions } from '@AppState/view-mode'
import { Outlet, useLocation } from 'react-router-dom'
import { fetchAppVersion } from '@AppState/app-version'
import { UserMessage } from '@Components/common/user-message'
import { DataFetchingState } from '@AppState/types'
import { AppBackground } from '@Components/app-background/app-background'
import { fetchMaintenanceHostConfig } from '@AppState/host-config'
import { ExitButton } from '@Components/exit-game-button/exit-game-button'
import { NAV_VALUES as TicketModeValues } from '@AppState/ticket-info/types'

const CLASS_NAME = 'main-app'

const handleExitClick = () => {
  ContainerMessenger.sendMessage(ContainerMessage.EXIT)
}

export const MainApp = () => {
  const dispatch = useAppDispatch()
  const location = useLocation()
  const { gameRules: gameConfig, fetchState: gameConfigState } = useSelector(
    (state: RootState) => state.gameConfig,
  )
  const appConfig = useSelector((state: RootState) => state.appConfig.config)
  const entryPoint = useSelector((state: RootState) => state.navigation.entryPoint)
  const { config: hostConfig, fetchState: hostConfigState } = useSelector(
    (state: RootState) => state.hostConfig,
  )
  const { latestDraw, fetchState: latestDrawState } = useSelector(
    (state: RootState) => state.latestDraw,
  )
  const appSettings = useSelector((state: RootState) => state.appSettings)
  const { canvasState: loadState } = useSelector((state: RootState) => state.animation)
  const ticketModeData = useSelector((state: RootState) => state.ticketMode)
  const activeMode = useSelector((state: RootState) => state.navigation.activeMode)

  const [hiddenRef, setHiddenRef] = useState<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!hiddenRef || !hostConfig || !gameConfig || !appConfig) return

    GameController.create(hiddenRef)

    return () => {
      GameController.destroy()
    }
  }, [hiddenRef, hostConfig, gameConfig, appConfig])

  const onRefSet = useCallback((node: HTMLDivElement) => {
    setHiddenRef(node)
  }, [])

  useEffect(() => {
    const vh = window.innerHeight * 0.01
    const vw = window.innerWidth * 0.01
    document.documentElement.style.setProperty('--vh', `${vh}px`)
    document.documentElement.style.setProperty('--vw', `${vw}px`)

    window.addEventListener('resize', () => {
      const vh = window.innerHeight * 0.01
      const vw = window.innerWidth * 0.01
      document.documentElement.style.setProperty('--vh', `${vh}px`)
      document.documentElement.style.setProperty('--vw', `${vw}px`)
    })
  }, [])

  useEffect(() => {
    if (!appConfig) {
      return
    }

    // If we're under maintenance we only want to fetch the host config - don't need to fetch preferences, etc
    if (appConfig.underMaintenance) {
      dispatch(fetchMaintenanceHostConfig())
      return
    }

    RapidGameDataServiceAPI.configureAPI({
      apiKey: appConfig.gameDataApiKey,
      basePath: appConfig.gameDataServiceUrl,
      clientId: appConfig.clientId,
      rapidGameId: appConfig.rapidGameId,
    })
    KenoDataServiceAPI.configureAPIs({
      gameData: {
        apiKey: appConfig.gameDataApiKey,
        basePath: appConfig.gameDataServiceUrl,
        clientId: appConfig.clientId,
        rapidGameId: appConfig.rapidGameId,
      },
      playerData: {
        apiKey: appConfig.playerDataApiKey,
        basePath: appConfig.playerDataServiceUrl,
        clientId: appConfig.clientId,
      },
    })
    dispatch(fetchHostAndAppConfig())
    dispatch(fetchGameConfig())
    dispatch(fetchAppVersion())
    dispatch(fetchLatestDraw())
  }, [dispatch, appConfig])

  const appReady = useCallback(() => {
    return gameConfig && appConfig && latestDraw && hostConfig && appSettings.synced
  }, [gameConfig, appConfig, latestDraw, hostConfig, appSettings.synced])

  useEffect(() => {
    if (appReady() && appConfig?.clientId) {
      setTimeout(() => {
        Analytics.trackUser(appConfig.hostName, appConfig.clientId, appConfig.playerId)
      }, 5000)
    }
  }, [appReady, appConfig])

  useEffect(() => {
    if (entryPoint && entryPoint === NAV_VALUES.VIEW_MODE) {
      dispatch(ViewModeActions.updateShouldShowWelcomeDrawer(true))
    }
  }, [dispatch, entryPoint])

  if (location.pathname !== '/keno-to-go') {
    return (
      <div>
        <AppConfigLoader />
        <Outlet />
      </div>
    )
  }

  const errorLoadingConfig =
    latestDrawState === DataFetchingState.FAILED ||
    hostConfigState === DataFetchingState.FAILED ||
    gameConfigState === DataFetchingState.FAILED

  if (errorLoadingConfig) {
    return (
      <UserMessage
        title="Oops! We're on it!"
        message="Sorry about the hiccup! We're working hard to fix things and get you back in the game. Thanks for your patience—we'll be up and running soon!"
      />
    )
  }

  const exitButtonColor = activeMode === NAV_VALUES.VIEW_MODE || (activeMode === NAV_VALUES.TICKET_MODE && ticketModeData.activeMenu === TicketModeValues.WATCH_TICKET) ? 'white' : 'black'

  if (!appReady()) {
    return (
      <div>
        <ExitButton className={`${CLASS_NAME}__exit-button`} onClick={() => handleExitClick()} fontColor={exitButtonColor} />
        <AppBackground />
        <AppConfigLoader />
        <LoadingAnimation />
      </div>
    )
  }

  const validAnimationState =
    loadState !== ANIMATION_CANVAS_STATE.INIT &&
    loadState !== ANIMATION_CANVAS_STATE.GAME_LOADING &&
    loadState !== ANIMATION_CANVAS_STATE.ASSETS_LOADING &&
    loadState !== ANIMATION_CANVAS_STATE.DESTROYING

  return (
    <div className={CLASS_NAME}>
      <ExitButton className={`${CLASS_NAME}__exit-button`} onClick={() => handleExitClick()} fontColor={exitButtonColor} />

      <AppBackground />
      {validAnimationState ? <NavigationBar /> : null}
      <div className={`${CLASS_NAME}__hidden`} ref={onRefSet}></div>
    </div>
  )
}
